import React, { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Single or multi image uploader using Emergent object storage.
 * Props:
 *   value: string | string[]   // current url(s)
 *   onChange(next)              // called with new url or url[]
 *   multi: boolean              // default false
 *   max: number                 // for multi mode
 */
export function Uploader({ value, onChange, multi = false, max = 12, label = "Upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const urls = multi ? (Array.isArray(value) ? value.filter(Boolean) : []) : (value ? [value] : []);

  const pick = () => inputRef.current?.click();

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const arr = Array.from(files);
      const list = multi ? [...urls] : [];
      for (const f of arr) {
        if (multi && list.length >= max) break;
        try {
          const url = await upload(f);
          list.push(url);
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
        }
      }
      onChange(multi ? list : list[0] || "");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (i) => {
    if (multi) {
      const next = urls.filter((_, idx) => idx !== i);
      onChange(next);
    } else {
      onChange("");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Button type="button" variant="outline" size="sm" onClick={pick} disabled={busy || (multi && urls.length >= max)} data-testid="uploader-pick-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
          {label}
          {multi && ` (${urls.length}/${max})`}
        </Button>
        <input ref={inputRef} type="file" accept="image/*" multiple={multi} hidden onChange={(e) => handleFiles(e.target.files)} data-testid="uploader-input" />
      </div>
      {urls.length > 0 && (
        <div className={`grid ${multi ? "grid-cols-3 md:grid-cols-4 gap-2" : "grid-cols-1"}`}>
          {urls.map((u, i) => (
            <div key={`${u}-${i}`} className="relative group rounded-md border border-zinc-200 bg-zinc-50 overflow-hidden">
              <img src={u} alt={`upload ${i + 1}`} className="w-full h-24 object-cover" />
              <button type="button" onClick={() => removeAt(i)} className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-white/90 border border-zinc-200 opacity-0 group-hover:opacity-100 transition" aria-label="remove" data-testid={`uploader-remove-${i}`}>
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
